import { useEffect, useState } from 'react'

import { isError, isSnapshot, subscribeFrame, toRosterRows } from '../lib/backendProtocol'

// How long a row stays highlighted after its ping state flips.
const FLASH_MS = 1500
// How long to wait before dialling the server again after the socket drops.
const RECONNECT_MS = 2000

// ---------------------------------------------------------------- the rows

// `flashUntil` is ours, not the backend's: it holds the moment this row's
// highlight should switch off. 0 means "not highlighted". Storing a deadline
// means we never have to set a timer and never have to cancel one.
function toRow(row) {
  return { ...row, flashUntil: 0 }
}

// Put rows into a Map keyed by id, so that finding one is a single lookup
// instead of a search through the whole list.
function indexById(rows) {
  const byId = new Map()

  for (const row of rows) {
    byId.set(row.id, row)
  }

  return byId
}

// Did anything the backend told us about this row actually change?
function hasChanged(before, incoming) {
  return (
    before.active !== incoming.active ||
    before.changedAt !== incoming.changedAt ||
    before.user !== incoming.user ||
    before.code !== incoming.code ||
    before.company !== incoming.company ||
    before.tallyActive !== incoming.tallyActive
  )
}

/**
 * Folds a fresh snapshot into the rows already on screen.
 *
 * The backend sends the complete list every time, so the simple thing would be
 * to throw away what we have and use the new list. We don't, for two reasons:
 *
 *  - a row that did not change is handed back as the very same object, which is
 *    how you tell React nothing happened to it;
 *  - we would lose `flashUntil`, so nothing would ever highlight.
 *
 * Because the result is built from the incoming list, a client that disappears
 * from the snapshot disappears from the table too.
 */
function mergeSnapshot(current, incoming) {
  const previous = indexById(current)
  const rows = []

  for (const row of incoming) {
    const before = previous.get(row.id)

    if (!before) {
      // A client we have never seen. Nothing to compare against, so no flash.
      rows.push(toRow(row))
      continue
    }

    if (!hasChanged(before, row)) {
      // Identical to what we already have. Keep the exact same object.
      rows.push(before)
      continue
    }

    // Only a real online <-> offline flip starts a new highlight. A row whose
    // company name changed should not blink as though it went offline.
    const flipped = before.active !== row.active
    let flashUntil = before.flashUntil
    if (flipped) {
      flashUntil = Date.now() + FLASH_MS
    }

    rows.push({ ...before, ...row, flashUntil })
  }

  return rows
}

// ---------------------------------------------------------------- the hook

/**
 * Keeps one WebSocket open to the dashboard server and keeps `users` in step
 * with whatever it pushes.
 *
 * The backend sends one kind of data message, `clients_snapshot`, containing the
 * complete client list. It pushes one when you connect, one whenever a
 * registration changes, and one whenever its own polling notices the table was
 * edited from outside. See `src/lib/backendProtocol.js` for the exact shape.
 *
 * Kept out on purpose, to keep this readable: exponential backoff, a heartbeat
 * watchdog for half-open sockets, and batching of very frequent pushes.
 */
export function useLiveRoster({ url, initialUsers = [] }) {
  // Empty until the server says otherwise. There is deliberately no placeholder
  // roster: every row on screen corresponds to a row the server sent.
  // `initialUsers` exists so a test can hand in rows without a socket.
  const [users, setUsers] = useState(initialUsers)
  const [status, setStatus] = useState(url ? 'connecting' : 'offline')
  // Null means "no snapshot has ever arrived", which is not the same as "the
  // last one arrived just now" — the difference matters the whole time the
  // socket is open but the server's database is unreachable.
  const [lastMessageAt, setLastMessageAt] = useState(null)

  useEffect(() => {
    // No backend configured — stay empty and do nothing at all.
    if (!url) return

    let socket = null
    let retryTimer = null
    // Flipped by the cleanup below. It is how `handleClose` can tell "the line
    // dropped" apart from "we hung up on purpose because the page is closing".
    let weHungUp = false

    function handleMessage(event) {
      // Everything arrives as text, so unpack it back into an object first.
      // A real network can deliver anything, so never trust it to be valid.
      let message
      try {
        message = JSON.parse(event.data)
      } catch {
        console.warn('Ignored a message that was not valid JSON')
        return
      }

      if (isError(message)) {
        // The server is up but could not read its database. Say so in the
        // console and leave the rows alone — stale data beats an empty table.
        console.error('Dashboard server reported:', message.message)
        return
      }

      if (!isSnapshot(message)) return

      setLastMessageAt(Date.now())

      const incoming = toRosterRows(message)
      setUsers((current) => mergeSnapshot(current, incoming))
    }

    function handleClose() {
      // We closed it ourselves, so there is nothing to recover from.
      if (weHungUp) return

      // The rows stay on screen. A stale list, clearly labelled, beats an empty
      // one that reads as "everybody just went offline".
      setStatus('reconnecting')
      retryTimer = setTimeout(connect, RECONNECT_MS)
    }

    function connect() {
      socket = new WebSocket(url)

      // Three functions handed to the browser, to be run later. We never call
      // them ourselves — the browser does, when each of these things happens.
      //
      // Opening the socket is not enough: the server serves dashboards and Tally
      // agents on the same path and only starts pushing snapshots once we say
      // which one we are. Sent again on every reconnect, since the server keeps
      // no memory of a socket that dropped.
      socket.onopen = () => {
        socket.send(subscribeFrame())
        setStatus('live')
      }
      socket.onmessage = handleMessage

      // `onerror` is deliberately not handled: the browser always fires
      // `onclose` straight after it, so keeping the retry in one place avoids
      // dialling twice for a single failure.
      socket.onclose = handleClose
    }

    connect()

    // React runs this when the page closes, or when `url` changes.
    return () => {
      weHungUp = true
      clearTimeout(retryTimer)
      if (socket) socket.close()
    }
  }, [url])

  return { users, status, lastMessageAt }
}
